import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useChatroom } from '../../contexts/ChatroomContext';
import { getChannelPath } from '../../services/utils';
import { CreateChannelModal } from '../CreateChannelModal';
import './ChannelList.css';

export function ChannelList({ onChannelSelect }) {
  const { channels, activeChannelId, selectChannel, isLoading, acceptInvitation, declineInvitation } = useChatroom();
  const navigate = useNavigate();
  const [processingInvite, setProcessingInvite] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const handleChannelClick = (channel) => {
    // Don't navigate if invitation is pending
    const hasPendingInvite = channel.current_user_participant &&
                             channel.current_user_participant.invitation_accepted === false;
    if (hasPendingInvite) {
      return;
    }

    // Navigate to channel URL (this triggers the URL param effect in ChatPage)
    const path = getChannelPath(channel.space_name || 'default', channel.name);
    navigate(path);
    // Also directly select for immediate feedback
    selectChannel(channel.id);
    // Notify parent (for mobile sidebar closing)
    onChannelSelect?.();
  };

  const handleAccept = async (e, channel) => {
    e.stopPropagation();
    setProcessingInvite(channel.id);
    try {
      await acceptInvitation(channel.id, channel.current_user_participant.id);
      // Navigate to the channel after accepting
      const path = getChannelPath(channel.space_name || 'default', channel.name);
      navigate(path);
      selectChannel(channel.id);
    } catch (error) {
      console.error('Failed to accept invitation:', error);
    } finally {
      setProcessingInvite(null);
    }
  };

  const handleDecline = async (e, channel) => {
    e.stopPropagation();
    setProcessingInvite(channel.id);
    try {
      await declineInvitation(channel.id, channel.current_user_participant.id);
    } catch (error) {
      console.error('Failed to decline invitation:', error);
    } finally {
      setProcessingInvite(null);
    }
  };

  const isPendingInvitation = (channel) => {
    return channel.current_user_participant &&
           channel.current_user_participant.invitation_accepted === false;
  };

  const handleChannelCreated = (channel) => {
    // Navigate to the newly created channel
    const path = getChannelPath(channel.space_name || 'default', channel.name);
    navigate(path);
    selectChannel(channel.id);
  };

  return (
    <div className="channel-list">
      <div className="channel-list-header">
        <h2>Channels</h2>
        <button
          className="create-channel-btn"
          onClick={() => setShowCreateModal(true)}
          title="Create new channel"
          aria-label="Create new channel"
        >
          +
        </button>
      </div>

      {showCreateModal && (
        <CreateChannelModal
          onClose={() => setShowCreateModal(false)}
          onChannelCreated={handleChannelCreated}
        />
      )}
      <div className="channel-list-items">
        {isLoading ? (
          <div className="loading-message">Loading channels...</div>
        ) : channels.length === 0 ? (
          <div className="empty-message">No channels available</div>
        ) : (
          channels.map(channel => {
            const pendingInvite = isPendingInvitation(channel);
            const isProcessing = processingInvite === channel.id;

            return (
              <div
                key={channel.id}
                className={`channel-item ${activeChannelId === channel.id ? 'active' : ''} ${pendingInvite ? 'pending-invite' : ''}`}
                onClick={() => handleChannelClick(channel)}
              >
                <div className="channel-info">
                  <div className="channel-name">#{channel.name}</div>
                  {channel.description && (
                    <div className="channel-description">{channel.description}</div>
                  )}
                </div>
                {pendingInvite && (
                  <div className="invitation-actions">
                    <span className="invite-label">Invited</span>
                    <button
                      className="accept-btn"
                      onClick={(e) => handleAccept(e, channel)}
                      disabled={isProcessing}
                    >
                      {isProcessing ? '...' : '✓'}
                    </button>
                    <button
                      className="decline-btn"
                      onClick={(e) => handleDecline(e, channel)}
                      disabled={isProcessing}
                    >
                      ✕
                    </button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
